import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import AutomationStepDetails from './AutomationStepDetails';
import { humanize, stepDetail } from './automationFormat';

/**
 * Compact numbered list of campaign steps or workflow actions.
 *
 * @param {object} props
 * @param {string} props.title - Section label, e.g. "Campaign Steps".
 * @param {Array} props.items - Steps/actions with step_type|action_type and config.
 */
export default function AutomationStepList({ title, items = [], depth = 0 }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-1">
      <span className="text-muted-foreground">{title} ({items.length})</span>
      <ol className="space-y-1 rounded-md border border-border bg-muted/40 px-2 py-1.5">
        {items.map((item, index) => {
          const detail = stepDetail(item.config);
          return (
            <li
              key={item.step_id || `${index}-${item.step_type || item.action_type}`}
              className="text-foreground"
            >
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto w-full justify-start gap-1.5 whitespace-normal px-0 py-1 text-left text-xs"
                    aria-label={`Review ${title} ${index + 1}`}
                  >
                    <span className="w-4 shrink-0 text-muted-foreground">{index + 1}.</span>
                    <span className="min-w-0 flex-1 break-words">
                      <span className="font-medium">{item.label || humanize(item.step_type || item.action_type)}</span>
                      {detail && <span className="text-muted-foreground">{` — ${detail}`}</span>}
                    </span>
                    <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 px-2 pb-2">
                  <AutomationStepDetails config={item.config} depth={depth} />
                </CollapsibleContent>
              </Collapsible>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
